import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { paymentProvidersService } from 'components/api/paymentProviders';
import { PaymentProvider, CreateProviderDto, UpdateProviderDto } from 'types/paymentProvider';

interface PaymentProvidersState {
  data: PaymentProvider[];
  isLoading: boolean;
  error: string | null;
}

const initialState: PaymentProvidersState = {
  data: [],
  isLoading: false,
  error: null,
};

export const fetchProviders = createAsyncThunk(
  'paymentProviders/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await paymentProvidersService.getProviders();
      return response.data; // Assumption: data format returns the array here
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Error fetching providers',
      );
    }
  },
);

export const createProvider = createAsyncThunk(
  'paymentProviders/create',
  async (data: CreateProviderDto, { rejectWithValue }) => {
    try {
      const response = await paymentProvidersService.createProvider(data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Error creating provider',
      );
    }
  },
);

export const updateProvider = createAsyncThunk(
  'paymentProviders/update',
  async ({ id, data }: { id: number; data: UpdateProviderDto }, { rejectWithValue }) => {
    try {
      const response = await paymentProvidersService.updateProvider(id, data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Error updating provider',
      );
    }
  },
);

export const deleteProvider = createAsyncThunk(
  'paymentProviders/delete',
  async (id: number, { rejectWithValue }) => {
    try {
      await paymentProvidersService.deleteProvider(id);
      return id;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Error deleting provider',
      );
    }
  },
);

const paymentProvidersSlice = createSlice({
  name: 'paymentProviders',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchProviders.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProviders.fulfilled, (state, action) => {
        state.isLoading = false;
        // Find the actual array whether it's deeply nested, lightly nested, or root.
        let parsedData = [];
        if (Array.isArray(action.payload)) {
          parsedData = action.payload;
        } else if (action.payload?.data?.providers) {
          parsedData = action.payload.data.providers;
        } else if (action.payload?.data) {
          parsedData = Array.isArray(action.payload.data)
            ? action.payload.data
            : action.payload.data.docs || [];
        }

        state.data = parsedData;
      })
      .addCase(fetchProviders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create
      .addCase(createProvider.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createProvider.fulfilled, (state, action) => {
        state.isLoading = false;
        const newProvider = action.payload?.data || action.payload;
        if (newProvider && newProvider.id) {
          state.data.push(newProvider);
        }
      })
      .addCase(createProvider.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Update
      .addCase(updateProvider.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProvider.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedProvider = action.payload?.data || action.payload;
        const index = state.data.findIndex((p) => p.id === updatedProvider.id);
        if (index !== -1) {
          state.data[index] = updatedProvider;
        }
      })
      .addCase(updateProvider.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Delete
      .addCase(deleteProvider.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteProvider.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = state.data.filter((p) => p.id !== action.payload);
      })
      .addCase(deleteProvider.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = paymentProvidersSlice.actions;

export default paymentProvidersSlice.reducer;
